import type { QueryClient } from '@tanstack/react-query';
import { removeWorkspaceScopedQueries } from '@vizit/api';
import * as React from 'react';

// Prevents A-out-B leaks: workspace A data must never render after switching to B, and must not
// survive a logout into the next login. Navigating to a non-workspace page (e.g. a portfolio grid)
// while still authenticated keeps the cache, so returning to the workspace does not refetch.
export function useClearWorkspaceCacheOnChange(
  queryClient: QueryClient,
  activeWorkspaceId: string | null,
  isAuthenticated = true,
): void {
  const previousWorkspaceIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const previous = previousWorkspaceIdRef.current;
    const switchedWorkspace = activeWorkspaceId !== null && previous !== activeWorkspaceId;
    const loggedOut = activeWorkspaceId === null && !isAuthenticated;

    if (previous && (switchedWorkspace || loggedOut)) {
      removeWorkspaceScopedQueries(queryClient, previous);
    }

    previousWorkspaceIdRef.current = activeWorkspaceId;
  }, [activeWorkspaceId, isAuthenticated, queryClient]);
}
